import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSocket, initializeSocket } from "../utils/socket";
import Navbar from "./Navbar";
import ChatTabs from "./ChatTabs";
import SendGift from "./SendGift"; // Thêm import SendGift
import ChatRestriction from "./ChatRestriction";
import { FaCrown, FaTrash, FaHeart, FaCopy, FaReply, FaShare, FaThumbtack, FaStar, FaUndo, FaGift } from "react-icons/fa";

import { FixedSizeList } from "react-window";
import { motion, AnimatePresence } from "framer-motion";

const ChatPage = () => {
  const { userId } = useParams();
  const { user: authUser, token, setToken, notifications, setNotifications, unreadNotificationCount, setUnreadNotificationCount, fetchWithRetry } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [targetUser, setTargetUser] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [isNotiFading, setIsNotiFading] = useState(false);
  const chatEndRef = useRef(null);
  const chatStartRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [markedMessages, setMarkedMessages] = useState([]);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const pendingMessageIdRef = useRef(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [recallTimeLimit, setRecallTimeLimit] = useState(60);
  const [showGiftModal, setShowGiftModal] = useState(false); // Thêm state để hiển thị
  const socketRef = useRef(null);
  const recentGiftMessageIdRef = useRef(null);

  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => {
      setIsNotiFading(true);
      setTimeout(() => {
        setNotification(null);
        setIsNotiFading(false);
      }, 300);
    }, 2000);
  }, []);

  const getAvatarUrl = useCallback((user) => {
    return user?.primaryPhoto?.photoUrl || user?.avatar || "https://placehold.co/50";
  }, []);

  const getVipIconColor = useCallback((role) => {
    switch (role) {
      case "vip_gold":
        return "text-yellow-500";
      case "vip_platinum":
        return "text-gray-300";
      case "vip_diamond":
        return "text-blue-500";
      default:
        return "text-yellow-500";
    }
  }, []);

  const getLastReceivedMessageId = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (
        messages[i].senderId !== authUser._id &&
        !(messages[i].isSystemMessage || messages[i].isHidden)
      ) {
        return messages[i]._id;
      }
    }
    return null;
  }, [messages, authUser]);

  const handleContextMenu = useCallback((e, message) => {
    e.preventDefault();
    setContextMenu({
      x: e.pageX,
      y: e.pageY,
      messageId: message._id,
      messageContent: message.content,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleLikeMessage = useCallback((messageId) => {
    const message = messages.find((msg) => msg._id === messageId);
    if (!message) return;

    const newLikedState = !message.liked;

    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === messageId ? { ...msg, liked: newLikedState } : msg
      )
    );

    const socket = getSocket();
    if (socket) {
      socket.emit("likeMessage", { messageId, liked: newLikedState }, (response) => {
        if (response?.error) {
          console.error("Lỗi từ server khi like tin nhắn:", response.error);
          showNotification(response.error.message || "Không thể like tin nhắn!", "error");
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === messageId ? { ...msg, liked: !newLikedState } : msg
            )
          );
        }
      });
    }

    closeContextMenu();
  }, [messages, closeContextMenu, showNotification]);


  const handleRecallMessage = useCallback(async (messageId) => {
    const message = messages.find((msg) => msg._id === messageId);
    if (!message) return;

    const sentTime = new Date(message.createdAt);
    const currentTime = new Date();
    const timeDiff = (currentTime - sentTime) / (1000 * 60);

    if (timeDiff > recallTimeLimit) {
      showNotification(`Tin nhắn không thể thu hồi, đã gửi hơn ${recallTimeLimit} phút.`, "error");
      closeContextMenu();
      return;
    }

    const previousMessages = [...messages];
    try {
      const socket = getSocket();
      if (socket) {
        socket.emit("recallMessage", { messageId }, (response) => {
          if (response?.error) {
            console.error("Lỗi từ server khi thu hồi tin nhắn:", response.error);
            showNotification(response.error.message || "Không thể thu hồi tin nhắn!", "error");
            setMessages(previousMessages);
          } else {
            showNotification("Thu hồi tin nhắn thành công!", "success");
          }
        });
      } else {
        const res = await fetchWithRetry({
          method: "post",
          url: "/api/chat/recall-message",
          data: { messageId },
        });
        showNotification(res.data.message || "Thu hồi tin nhắn thành công!", "success");
      }
      closeContextMenu();
    } catch (error) {
      console.error("Lỗi khi thu hồi tin nhắn:", error);
      setMessages(previousMessages);
      showNotification(
        error.response?.data?.message || "Lỗi khi thu hồi tin nhắn, vui lòng thử lại!",
  "error"
      );
    }
  }, [fetchWithRetry, messages, showNotification, closeContextMenu, recallTimeLimit]);

  const handleCopyMessage = useCallback((content) => {
    navigator.clipboard.writeText(content).then(() => {
      showNotification("Đã sao chép tin nhắn!", "success");
    });
    closeContextMenu();
  }, [showNotification, closeContextMenu]);

  const handleReplyMessage = useCallback((messageId) => {
    setReplyToMessage(messages.find((msg) => msg._id === messageId));
    closeContextMenu();
  }, [messages, closeContextMenu]);

  const handleShareMessage = useCallback((messageId) => {
    showNotification("Chia sẻ tin nhắn: Chức năng đang phát triển!", "info");
    closeContextMenu();
  }, [showNotification, closeContextMenu]);

  const handlePinMessage = useCallback((messageId) => {
    const action = pinnedMessages.includes(messageId) ? "unpin" : "pin";

    setPinnedMessages((prev) =>
      action === "pin"
        ? [...prev, messageId]
        : prev.filter((id) => id !== messageId)
    );

    const socket = getSocket();
    if (socket) {
      socket.emit("pinMessage", { messageId, action }, (response) => {
        if (response?.error) {
          console.error(`Lỗi từ server khi ${action} tin nhắn:`, response.error);
          setPinnedMessages((prev) =>
            action === "pin"
              ? prev.filter((id) => id !== messageId)
              : [...prev, messageId]
          );
          showNotification(response.error.message || `Không thể ${action} tin nhắn!`, "error");
        } else {
          setPinnedMessages(response.data.map((pin) => pin.messageId));
          showNotification(`Đã ${action === "pin" ? "ghim" : "bỏ ghim"} tin nhắn thành công!`, "success");
        }
      });
    }

    closeContextMenu();
  }, [pinnedMessages, closeContextMenu, showNotification]);

  const handleMarkMessage = useCallback((messageId) => {
    setMarkedMessages((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId]
    );
    closeContextMenu();
  }, [closeContextMenu]);

  const refreshChatHistory = useCallback(async () => {
    try {
      const msgRes = await fetchWithRetry({
        method: "get",
        url: `/api/chat/history/${userId}`,
      });
      const fetchedMessages = msgRes.data.messages || [];
      console.log("Dữ liệu lịch sử tin nhắn từ API:", fetchedMessages);

      const formattedMessages = fetchedMessages.map((msg) => ({
        ...msg,
        createdAt: new Date(msg.createdAt),
        readTimestamp: msg.readTimestamp ? new Date(msg.readTimestamp) : null,
        isTemp: false,
        liked: msg.liked || false,
        isSystemMessage: msg.isSystemMessage || false,
        originalMessageId: msg.originalMessageId || null,
      }));

      setMessages((prev) => {
        const updatedMessages = [...prev];
        formattedMessages.forEach((newMsg) => {
          const existingIndex = updatedMessages.findIndex(
            (msg) =>
              (msg._id === newMsg._id) ||
              (msg.isSystemMessage && msg.originalMessageId && msg.originalMessageId === newMsg.originalMessageId)
          );
          if (existingIndex !== -1) {
            updatedMessages[existingIndex] = newMsg;
          } else {
            updatedMessages.push(newMsg);
          }
        });
        return updatedMessages;
      });
    } catch (error) {
      console.error("Lỗi khi làm mới lịch sử tin nhắn:", error);
      showNotification(
        error.response?.data?.message || "Không thể tải lịch sử tin nhắn!",
        "error"
      );
    }
  }, [fetchWithRetry, userId, showNotification]);


  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim()) {
      console.log("Tin nhắn trống, không gửi.");
      return;
    }

    if (!authUser?._id || !userId) {
      console.error("Dữ liệu người dùng không hợp lệ:", { authUser, userId });
      setError("Dữ liệu người dùng không hợp lệ!");
      return;
    }

    if (!targetUser) {
      console.error("Người nhận (targetUser) chưa được tải!");
      setError("Không thể gửi tin nhắn vì thông tin người nhận chưa được tải!");
      return;
    }

    const messageData = {
      senderId: authUser._id,
      receiverId: userId,
      content: newMessage,
      replyTo: replyToMessage ? replyToMessage._id : null,
    };

    setNewMessage("");
    setReplyToMessage(null);

    try {
      let socket = getSocket();
      if (!socket && token) {
        console.log("Socket chưa sẵn sàng, khởi tạo...");
        socket = initializeSocket(token);
        socketRef.current = socket;
        await new Promise((resolve) => {
          socket.on("connect", () => {
            console.log("Socket khởi tạo thành công!");
            resolve();
          });
          socket.on("connect_error", (err) => {
            console.warn("Không thể khởi tạo socket:", err.message);
            showNotification("Không thể kết nối tới server thông báo.", "error");
            resolve();
          });
          setTimeout(resolve, 5000);
        });
      }

      if (socket && socket.connected) {
        console.log("Gửi tin nhắn qua Socket.IO:", messageData);
        socket.emit("sendMessage", messageData, (response) => {
          if (response?.error) {
            console.error("Lỗi từ server khi gửi tin nhắn qua Socket.IO:", response.error);
            showNotification(response.error.message || "Không thể gửi tin nhắn!", "error");
          } else {
            console.log("Tin nhắn gửi thành công qua Socket.IO:", response.data);
            pendingMessageIdRef.current = response.data._id;
            setTimeout(() => {
              pendingMessageIdRef.current = null;
            }, 2000);
          }
        });
      } else {
        console.warn("Socket.IO không kết nối, gửi tin nhắn qua API trực tiếp...");
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/chat/send",
          data: messageData,
        });

        console.log("Tin nhắn gửi thành công qua API:", response.data);
        pendingMessageIdRef.current = response.data._id;
        setTimeout(() => {
          pendingMessageIdRef.current = null;
        }, 2000);
      }
    } catch (error) {
      console.error("Lỗi khi gửi tin nhắn:", error);
      showNotification(error.response?.data?.message || "Không thể gửi tin nhắn!", "error");
    }
  }, [newMessage, authUser, userId, replyToMessage, fetchWithRetry, token, showNotification, targetUser]);

  const handleChatMessage = useCallback((message) => {
    console.log("Nhận sự kiện chatMessage từ server:", message);
  
    const isRelevantMessage =
      (message.senderId === authUser._id && message.receiverId === userId) ||
      (message.senderId === userId && message.receiverId === authUser._id) ||
      (message.isSystemMessage &&
        ((message.originalSenderId === authUser._id && message.originalReceiverId === userId) ||
         (message.originalSenderId === userId && message.originalReceiverId === authUser._id)));
  
    if (!isRelevantMessage) {
      console.log("Bỏ qua tin nhắn không thuộc cuộc trò chuyện hiện tại:", message);
      return;
    }
  
    const formattedMessage = {
      ...message,
      createdAt: new Date(message.createdAt),
      readTimestamp: message.readTimestamp ? new Date(message.readTimestamp) : null,
      isTemp: false,
      liked: message.liked || false,
      replyTo: message.replyTo || null,
      isSystemMessage: message.isSystemMessage || false,
      isGift: message.isGift || false, // Đảm bảo trường isGift được bao gồm
      gift: message.gift || null, // Đảm bảo trường gift được bao gồm
      originalMessageId: message.originalMessageId || null,
      originalSenderId: message.originalSenderId || null,
      originalReceiverId: message.originalReceiverId || null,
    };
  
    setMessages((prev) => {
      const existingMessageIndex = prev.findIndex(
        (msg) =>
          msg._id === message._id ||
          (msg.isSystemMessage &&
           msg.originalMessageId &&
           msg.originalMessageId === message.originalMessageId)
      );
  
      if (existingMessageIndex !== -1) {
        console.log("Cập nhật tin nhắn/thông báo hiện có:", formattedMessage);
        const updatedMessages = [...prev];
        updatedMessages[existingMessageIndex] = formattedMessage;
        return updatedMessages;
      }
  
      console.log("Thêm tin nhắn/thông báo mới:", formattedMessage);
      return [...prev, formattedMessage];
    });
  
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [authUser, userId]);
  useEffect(() => {
    if (!authUser || !token || !userId) return;
  
    setMessages([]);
    setPinnedMessages([]);
    setMarkedMessages([]);
    setSelectedMessages([]);
    setExpandedMessages({});
  
    const fetchChatData = async () => {
      try {
        setLoading(true);
        setError(null);
  
        const userRes = await fetchWithRetry({
          method: "get",
          url: `/api/chat/users/${userId}`,
        });
        const userData = userRes.data;
        setTargetUser({
          ...userData,
          role: userData.role || "user",
          primaryPhoto: userData.primaryPhoto || { photoUrl: "", caption: "" },
          avatar: userData.avatar || "",
          isOnline: userData.isOnline ?? false,
          membershipId: userData.membershipId || userData._id,
          locationText: userData.locationText || { city: "Không xác định" },
        });
  
        await refreshChatHistory();
  
        const fetchPinnedMessages = async () => {
          const conversationId = [authUser._id, userId].sort().join("_");
          const cachedPinned = localStorage.getItem(`pinnedMessages_${conversationId}`);
          if (cachedPinned) {
            setPinnedMessages(JSON.parse(cachedPinned));
          }
          try {
            const pinnedRes = await fetchWithRetry({
              method: "get",
              url: `/api/chat/pinned-messages/${conversationId}`,
            });
            const pinnedData = pinnedRes.data || [];
            setPinnedMessages(pinnedData.map((pin) => pin.messageId));
            localStorage.setItem(`pinnedMessages_${conversationId}`, JSON.stringify(pinnedData.map((pin) => pin.messageId)));
          } catch (pinnedError) {
            console.error("Lỗi khi tải danh sách tin nhắn được ghim:", pinnedError);
            setPinnedMessages([]);
            showNotification("Không thể tải danh sách tin nhắn được ghim!", "error");
          }
        };
        await fetchPinnedMessages();
  
        const savedLimit = localStorage.getItem('recallTimeLimit');
        if (savedLimit) {
          setRecallTimeLimit(parseInt(savedLimit));
        }
      } catch (err) {
        let errorMessage = "Không thể tải dữ liệu chat!";
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = "Không tìm thấy người dùng!";
          } else if (err.response.status === 403) {
            errorMessage = err.response.data.message || "Bạn không có quyền truy cập cuộc trò chuyện này!";
          } else if (err.response.status === 401) {
            errorMessage = "Phiên làm việc hết hạn. Vui lòng đăng nhập lại.";
          } else if (err.response.status === 500) {
            errorMessage = "Server gặp sự cố, vui lòng thử lại sau.";
          } else {
            errorMessage = err.response.data?.message || "Không thể tải dữ liệu chat!";
          }
        } else if (err.message.includes("Network Error")) {
          errorMessage = "Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng và thử lại!";
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
  
    fetchChatData();
  
    let socket = getSocket();
    if (!socket && token) {
      console.log("Socket chưa sẵn sàng, khởi tạo...");
      socket = initializeSocket(token);
      socketRef.current = socket;
    }
  
    if (!socket) {
      console.warn("Socket không khởi tạo được, bỏ qua kết nối WebSocket");
      showNotification("Không thể kết nối tới server thông báo.", "error");
      return;
    }
  
    socketRef.current = socket;
  
    const joinChatRoom = () => {
      if (authUser?._id && userId) {
        const roomId = [authUser._id, userId].sort().join("_");
        socket.emit("joinChatRoom", { userId: authUser._id, targetUserId: userId });
        console.log(`Client ${authUser._id} tham gia room ${roomId}`);
        socket.on("joinedRoom", async ({ roomId: joinedRoomId }) => {
          console.log(`Client ${authUser._id} đã tham gia room ${joinedRoomId}`);
          await refreshChatHistory();
        });
      }
    };
  
    socket.on("connect", () => {
      console.log("Socket.IO đã kết nối thành công");
      setIsReconnecting(false);
      joinChatRoom();
    });
  
    socket.on("connect_error", (err) => {
      console.error("Socket.IO connect error:", err);
      setIsReconnecting(true);
      showNotification("Không thể kết nối tới server thông báo. Đang thử lại...", "error");
    });
  
    socket.on("disconnect", (reason) => {
      console.log("Socket.IO disconnected:", reason);
      setIsReconnecting(true);
      if (reason !== "io client disconnect") {
        setTimeout(() => {
          socket.connect();
        }, 3000);
      }
    });
  
    socket.on("auth_error", async ({ message }) => {
      console.error("Lỗi xác thực Socket.IO:", message);
      try {
        const newToken = await fetchWithRetry({
          method: "post",
          url: "/api/users/auth/refresh-token",
          data: { refreshToken: localStorage.getItem("refreshToken") },
        });
        setToken(newToken.data.accessToken);
        socket.auth.token = `Bearer ${newToken.data.accessToken}`;
        socket.connect();
      } catch (error) {
        showNotification("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.", "error");
        navigate("/login");
      }
    });
  
    socket.on("chatMessage", handleChatMessage);
  
    socket.on("messageLiked", ({ _id, liked }) => {
      console.log(`Nhận sự kiện messageLiked: _id=${_id}, liked=${liked}`);
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === _id ? { ...msg, liked } : msg
        )
      );
    });
  
    socket.on("messageRecalled", ({ messageId, recalledMessage }) => {
      console.log(`Nhận sự kiện messageRecalled: messageId=${messageId}`);
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === messageId ? recalledMessage : msg
        )
      );
      setPinnedMessages((prev) => prev.filter((id) => id !== messageId));
      setMarkedMessages((prev) => prev.filter((id) => id !== messageId));
    });
  
    socket.on("messagesDeleted", async ({ messageIds, userId: deleterId }) => {
      console.log(`Nhận sự kiện messagesDeleted từ user ${deleterId}:`, messageIds);
      setMessages((prev) => prev.filter((msg) => !messageIds.includes(msg._id)));
      setPinnedMessages((prev) => prev.filter((id) => !messageIds.includes(id)));
      setMarkedMessages((prev) => prev.filter((id) => !messageIds.includes(id)));
      setSelectedMessages((prev) => prev.filter((id) => !messageIds.includes(id)));
      await refreshChatHistory();
    });
  
    socket.on("allMessagesDeleted", async ({ userId: deleterId, targetUserId }) => {
      console.log(`Nhận sự kiện allMessagesDeleted từ user ${deleterId}`);
      if (deleterId === authUser._id || targetUserId === userId) {
        setMessages([]);
        setPinnedMessages([]);
        setMarkedMessages([]);
        await refreshChatHistory();
      }
    });
  
    socket.on("pinnedMessagesUpdated", ({ conversationId, pinnedMessages: updatedPinned }) => {
      const expectedConversationId = [authUser._id, userId].sort().join("_");
      if (conversationId === expectedConversationId) {
        console.log(`Nhận sự kiện pinnedMessagesUpdated cho conversation ${conversationId}:`, updatedPinned);
        setPinnedMessages(updatedPinned.map((pin) => pin.messageId));
      }
    });
  
    // 3. Thay thế khối socket.on("notification", ...)
socket.on("notification", (notificationData) => {
  console.log("Nhận thông báo từ server:", notificationData);
  if (notificationData.type === "pin_limit_reached") {
    showNotification(notificationData.message, "warning");
  } else if (
    notificationData.type === "chat_restriction_updated" ||
    notificationData.type === "hide_messages_toggled" ||
    notificationData.type === "hide_messages_with_user_toggled"
  ) {
    refreshChatHistory();
    showNotification(notificationData.message, "info");
  } else if (notificationData.type === "gift" && notificationData.userId === userId) {
    // Chỉ thêm tin nhắn quà tặng nếu người dùng hiện tại là người nhận
    if (authUser._id !== notificationData.userId) {
      const giftMessage = {
        _id: `gift-${Date.now()}`,
        senderId: userId,
        receiverId: authUser._id,
        content: "[GIFT]",
        createdAt: new Date(),
        isSystemMessage: false,
        isGift: true,
        gift: {
          id: notificationData.giftId,
          name: notificationData.message.split("một ")[1].split("!")[0],
          imageUrl: notificationData.giftImageUrl,
          coinPrice: notificationData.giftCoinPrice,
        },
      };
      setMessages((prev) => {
        // Kiểm tra xem tin nhắn quà tặng đã tồn tại hay chưa
        const exists = prev.some(
          (msg) =>
            msg.isGift &&
            msg.gift.id === giftMessage.gift.id &&
            msg.senderId === giftMessage.senderId &&
            Math.abs(msg.createdAt.getTime() - giftMessage.createdAt.getTime()) < 1000
        );
        if (exists || recentGiftMessageIdRef.current === giftMessage._id) {
          console.log("Bỏ qua tin nhắn quà tặng trùng lặp:", giftMessage);
          return prev;
        }
        console.log("Thêm tin nhắn quà tặng mới:", giftMessage);
        return [...prev, giftMessage];
      });
      showNotification(notificationData.message, "info");
    }
  } else {
    showNotification(
      `${notificationData.message} ❤️`,
      notificationData.type === "like" ? "info" : "warning"
    );
  }
});
  
    if (socket.connected) {
      joinChatRoom();
    }
  
    return () => {
      socket.off("connect");
      socket.off("connect_error");
      socket.off("disconnect");
      socket.off("auth_error");
      socket.off("chatMessage");
      socket.off("messageLiked");
      socket.off("messageRecalled");
      socket.off("messagesDeleted");
      socket.off("allMessagesDeleted");
      socket.off("pinnedMessagesUpdated");
      socket.off("notification");
  
      if (authUser?._id && userId) {
        const roomId = [authUser._id, userId].sort().join("_");
        socket.emit("leaveChatRoom", { roomId });
        console.log(`Client ${authUser._id} rời room ${roomId}`);
      }
    };
  }, [authUser, token, setToken, userId, fetchWithRetry, handleChatMessage, navigate, showNotification, refreshChatHistory]);
  
  const handleMessageRead = useCallback((message) => {
    if (
      (message.senderId === userId && message.receiverId === authUser._id) ||
      (message.senderId === authUser._id && message.receiverId === userId)
    ) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === message._id
            ? {
                ...msg,
                read: true,
                status: "read",
                readTimestamp: new Date(message.readTimestamp),
              }
            : msg
        )
      );
    }
  }, [authUser, userId]);

      useEffect(() => {
        if (!authUser || !userId) return;

        const socket = getSocket();
        if (!socket) {
          console.warn("Socket chưa sẵn sàng, bỏ qua xử lý tin nhắn");
          return;
        }

        socket.on("chatMessage", handleChatMessage);
        socket.on("messageRead", handleMessageRead);

        return () => {
          socket.off("chatMessage", handleChatMessage);
          socket.off("messageRead", handleMessageRead);
        };
      }, [authUser, userId, messages, showNotification, token, targetUser, handleChatMessage, handleMessageRead]);

      
  const handleDeleteMessage = useCallback(async (messageId) => {
    try {
      await fetchWithRetry({
        method: "post",
        url: "/api/chat/delete-messages",
        data: { messageIds: [messageId] },
      });
      showNotification("Xóa tin nhắn thành công!", "success");
      closeContextMenu();
      await refreshChatHistory(); // Làm mới lịch sử tin nhắn từ server
    } catch (error) {
      console.error("Lỗi khi xóa tin nhắn:", error);
      showNotification(
        error.response?.data?.message || "Lỗi khi xóa tin nhắn, vui lòng thử lại!",
        "error"
      );
    }
  }, [fetchWithRetry, showNotification, closeContextMenu, refreshChatHistory]);
  
  const handleDeleteSelectedMessages = useCallback(async () => {
    const previousMessages = [...messages]; // Lưu trạng thái trước khi xóa để khôi phục nếu lỗi
    try {
      // Cập nhật trạng thái cục bộ ngay lập tức để loại bỏ tin nhắn khỏi giao diện
      setMessages((prev) => prev.filter((msg) => !selectedMessages.includes(msg._id)));
      setPinnedMessages((prev) => prev.filter((id) => !selectedMessages.includes(id)));
      setMarkedMessages((prev) => prev.filter((id) => !selectedMessages.includes(id)));
      setSelectedMessages([]); // Xóa danh sách tin nhắn đã chọn
  
      // Gửi yêu cầu xóa đến server
      await fetchWithRetry({
        method: "post",
        url: "/api/chat/delete-messages",
        data: { messageIds: selectedMessages },
      });
  
      showNotification("Xóa các tin nhắn đã chọn thành công!", "success");
      closeContextMenu();
      await refreshChatHistory(); // Làm mới lịch sử tin nhắn để đồng bộ với server
    } catch (error) {
      console.error("Lỗi khi xóa các tin nhắn đã chọn:", error);
      setMessages(previousMessages); // Khôi phục trạng thái nếu lỗi
      showNotification(
        error.response?.data?.message || "Lỗi khi xóa các tin nhắn, vui lòng thử lại!",
        "error"
      );
    }
  }, [fetchWithRetry, messages, selectedMessages, showNotification, closeContextMenu, refreshChatHistory]);
  
  const handleDeleteAllMessages = useCallback(async () => {
    if (!userId) return;
  
    if (!window.confirm("Bạn có chắc chắn muốn xóa tất cả tin nhắn trong lịch sử trò chuyện của bạn không?")) {
      return;
    }
  
    const previousMessages = [...messages]; // Lưu trạng thái trước khi xóa để khôi phục nếu lỗi
    try {
      // Cập nhật trạng thái cục bộ ngay lập tức để xóa toàn bộ lịch sử trò chuyện
      setMessages([]);
      setPinnedMessages([]);
      setMarkedMessages([]);
  
      // Gửi yêu cầu xóa đến server
      await fetchWithRetry({
        method: "delete",
        url: `/api/chat/delete-all/${userId}`,
      });
  
      showNotification("Đã xóa tất cả tin nhắn trong lịch sử của bạn!", "success");
      closeContextMenu();
      await refreshChatHistory(); // Làm mới lịch sử tin nhắn để đồng bộ với server
    } catch (error) {
      console.error("Lỗi khi xóa tất cả tin nhắn:", error);
      setMessages(previousMessages); // Khôi phục trạng thái nếu lỗi
      showNotification(
        error.response?.data?.message || "Lỗi khi xóa tất cả tin nhắn, vui lòng thử lại!",
        "error"
      );
    }
  }, [userId, fetchWithRetry, messages, showNotification, closeContextMenu, refreshChatHistory]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Delete" && selectedMessages.length > 0) {
        handleDeleteSelectedMessages();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMessages, handleDeleteSelectedMessages]);

  const handleShareSelectedMessages = useCallback(() => {
    const selectedContent = messages
      .filter((msg) => selectedMessages.includes(msg._id))
      .map((msg) => msg.content)
      .join("\n");
    navigator.clipboard.writeText(selectedContent).then(() => {
      showNotification("Đã sao chép các tin nhắn để chia sẻ!", "success");
      setSelectedMessages([]);
      closeContextMenu();
    });
  }, [messages, selectedMessages, showNotification, closeContextMenu]);


  useEffect(() => {
    if (!authUser || !userId) return;

    const socket = getSocket();
    if (!socket) {
      console.warn("Socket chưa sẵn sàng, bỏ qua đánh dấu tin nhắn đã đọc");
      return;
    }

    const unreadMessages = messages.filter(
      (msg) => msg.senderId === userId && msg.receiverId === authUser._id && !msg.read
    );

    unreadMessages.forEach((msg) => {
      socket.emit("markMessageAsRead", msg._id);
    });
  }, [messages, authUser, userId, token]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    } else if (e.key === "Enter" && e.shiftKey) {
      setNewMessage((prev) => prev + "\n");
    }
  }, [handleSendMessage]);

  const handleBackToChatList = useCallback(() => {
    navigate("/chat-list");
    closeContextMenu();
  }, [navigate, closeContextMenu]);

  const handleToggleNotifications = useCallback(async (notificationId, action) => {
    try {
      if (action === "read") {
        const res = await fetchWithRetry({
          method: "put",
          url: `/api/users/notifications/${notificationId}/read`,
          data: {},
        });
        if (res.status === 200) {
          setNotifications((prev) =>
            prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
          );
          setUnreadNotificationCount((prev) => prev - 1);
        }
      } else if (action === "delete") {
        const res = await fetchWithRetry({
          method: "delete",
          url: `/api/users/notifications/${notificationId}`,
        });
        if (res.status === 200) {
          setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
          setUnreadNotificationCount((prev) => {
            const deletedNotification = notifications.find((n) => n._id === notificationId);
            return deletedNotification && !deletedNotification.read ? prev - 1 : prev;
          });
        }
      }
    } catch (error) {
      console.error(`Lỗi khi ${action} thông báo:`, error);
      showNotification(
        `Không thể ${action === "read" ? "đánh dấu đã đọc" : "xóa"} thông báo. Vui lòng thử lại!`,
        "error"
      );
    }
  }, [fetchWithRetry, notifications, setNotifications, setUnreadNotificationCount, showNotification]);

  const getLastSenderMessageIndex = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].senderId === authUser._id) {
        return messages[i]._id;
      }
    }
    return null;
  }, [messages, authUser]);

  const handleSendGift = useCallback(async (gift, coinBalance) => {
    if (!userId) {
      showNotification("Không thể gửi quà: Người nhận không hợp lệ!", "error");
      return;
    }
    if (!gift || !gift._id) {
      showNotification("Không thể gửi quà: Quà tặng không hợp lệ!", "error");
      return;
    }
  
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/users/send-gift",
        data: { receiverId: userId, giftId: gift._id },
      });
  
      if (response.status === 200) {
        showNotification(`Đã gửi quà "${gift.name}" thành công!`, "success");
  
        const giftMessage = {
          _id: `gift-${Date.now()}`,
          senderId: authUser._id,
          receiverId: userId,
          content: "[GIFT]",
          createdAt: new Date(),
          isSystemMessage: false,
          isGift: true,
          gift: {
            id: gift._id,
            name: gift.name,
            imageUrl: gift.imageUrl,
            coinPrice: gift.coinPrice,
          },
        };
  
        recentGiftMessageIdRef.current = giftMessage._id; // Lưu _id của tin nhắn quà tặng
        setMessages((prev) => {
          // Kiểm tra xem tin nhắn quà tặng đã tồn tại hay chưa
          const exists = prev.some(
            (msg) =>
              msg.isGift &&
              msg.gift.id === giftMessage.gift.id &&
              msg.senderId === giftMessage.senderId &&
              Math.abs(msg.createdAt.getTime() - giftMessage.createdAt.getTime()) < 1000
          );
          if (exists) return prev;
          return [...prev, giftMessage];
        });
        setShowGiftModal(false);
      } else {
        throw new Error("Phản hồi từ server không hợp lệ!");
      }
    } catch (error) {
      console.error("Lỗi khi gửi quà tặng:", error);
      let errorMessage = "Không thể gửi quà tặng, vui lòng thử lại!";
      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = error.response.data.message || "Dữ liệu gửi đi không hợp lệ!";
        } else if (error.response.status === 500) {
          errorMessage = "Lỗi server, vui lòng thử lại sau!";
        } else {
          errorMessage = error.response.data.message || errorMessage;
        }
      }
      showNotification(errorMessage, "error");
    }
  }, [fetchWithRetry, userId, authUser, showNotification]);
  

  const lastSenderMessageId = getLastSenderMessageIndex();
  const totalUnread = messages.filter(
    (msg) => msg.status !== "read" && msg.receiverId === authUser._id
  ).length;

  useEffect(() => {
    if (messages.length > 0) {
      if (messages.length * 100 < (window.innerHeight - 300)) {
        chatStartRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages]);






  
  const Row = ({ index, style }) => {
    const msg = messages[index];
    if (
      !(
        (msg.senderId === authUser._id && msg.receiverId === userId) ||
        (msg.senderId === userId && msg.receiverId === authUser._id) ||
        (msg.isSystemMessage &&
          ((msg.originalSenderId === authUser._id && msg.originalReceiverId === userId) ||
           (msg.originalSenderId === userId && msg.originalReceiverId === authUser._id)))
      )
    ) {
      return null;
    }
  
    console.log("Tin nhắn trong khung lịch sử:", msg);
  
    const isValidCreatedAt = msg.createdAt instanceof Date && !isNaN(msg.createdAt);
    const createdAtTime = isValidCreatedAt
      ? `${msg.createdAt.getDate()} tháng ${msg.createdAt.getMonth() + 1} năm ${msg.createdAt.getFullYear()} ${msg.createdAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
      : "Không xác định";
  
    const isValidReadTimestamp =
      msg.readTimestamp instanceof Date && !isNaN(msg.readTimestamp);
    const readTimestampTime = isValidReadTimestamp
      ? `${msg.readTimestamp.getDate()} thg ${msg.readTimestamp.getMonth() + 1} năm ${msg.readTimestamp.getFullYear()} ${msg.readTimestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
      : msg.read ? "Chưa có thời gian" : "Chưa xem";
  
    const receiverAvatar = targetUser;
    const lastReceivedMessageId = getLastReceivedMessageId();
    const showLikeButton =
      (msg._id === lastReceivedMessageId ||
        (msg.isSystemMessage && msg.originalMessageId === lastReceivedMessageId)) &&
      msg.senderId !== authUser._id;
  
    const isSelected = selectedMessages.includes(msg._id);
    const isExpanded = expandedMessages[msg._id] || false;
    const maxLines = 5;
    const lines = msg.content.split('\n');
    const displayContent = isExpanded || lines.length <= maxLines ? msg.content : lines.slice(0, maxLines).join('\n') + '...';
  
    const toggleExpand = () => {
      setExpandedMessages((prev) => ({
        ...prev,
        [msg._id]: !isExpanded,
      }));
    };
  
    const shouldShowSystemMessage = msg.isSystemMessage;
    const isCurrentUserUserRole = authUser.role === "user";
    const isGiftMessage = msg.isGift; // Kiểm tra nếu là tin nhắn quà tặng
  
    // Điều chỉnh nội dung thông báo hệ thống dựa trên vai trò
    let adjustedDisplayContent = displayContent;
    if (shouldShowSystemMessage) {
      if (msg.originalSenderId === authUser._id) {
        adjustedDisplayContent = `${targetUser?.name || "Người nhận"} Không thể đọc tin nhắn ngay bây giờ vì bạn là hội viên thường MIỄN PHÍ.`;
      } else if (msg.originalReceiverId === authUser._id) {
        adjustedDisplayContent = `Bạn có tin nhắn từ ${msg.originalSenderId === targetUser?._id ? targetUser.name : "Người gửi"} nhưng chưa thể đọc tin nhắn ngay bây giờ vì bạn là hội viên thường MIỄN PHÍ.`;
      }
    }
  
    return (
      <div
        style={style}
        key={msg._id}
        className={`mb-4 flex relative ${
          (msg.senderId === authUser._id || (msg.isSystemMessage && msg.originalSenderId === authUser._id))
            ? "justify-end"
            : "justify-start"
        } ${markedMessages.includes(msg._id) ? "bg-yellow-50 p-2 rounded" : ""} ${
          isSelected ? "bg-blue-100 border border-blue-500" : ""
        }`}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey) {
            setSelectedMessages((prev) =>
              prev.includes(msg._id)
                ? prev.filter((id) => id !== msg._id)
                : [...prev, msg._id]
            );
          }
        }}
      >
        {shouldShowSystemMessage ? (
          // Giữ nguyên hiển thị thông báo hệ thống
          (msg.senderId === authUser._id || (msg.isSystemMessage && msg.originalSenderId === authUser._id)) ? (
            <div className="flex flex-col items-end">
              <span className="text-xs text-gray-500 mb-1 self-end">
                {isValidCreatedAt ? createdAtTime : "Không xác định"}
              </span>
              <div
                className="relative w-fit max-w-md p-2 bg-yellow-100 text-gray-800 rounded-xl shadow-md"
                onContextMenu={(e) => handleContextMenu(e, msg)}
              >
                <div style={{ whiteSpace: 'pre-line' }}>
                  {adjustedDisplayContent}
                  {isCurrentUserUserRole && adjustedDisplayContent !== "Tin nhắn đã được thu hồi" && (
                    <button
                      onClick={() => navigate("/upgrade")}
                      className="ml-2 text-blue-500 underline hover:text-blue-700"
                    >
                      Nâng cấp ngay
                    </button>
                  )}
                </div>
                {lines.length > maxLines && (
                  <button
                    onClick={toggleExpand}
                    className="text-blue-500 text-xs mt-1"
                  >
                    {isExpanded ? "Thu gọn" : "Xem thêm"}
                  </button>
                )}
                {showLikeButton && (
                  <div className="absolute bottom-1 right-1">
                    {msg.liked ? (
                      <FaHeart
                        className="text-red-500 cursor-pointer"
                        size={12}
                        onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                      />
                    ) : (
                      <FaHeart
                        className="text-gray-400 cursor-pointer"
                        size={12}
                        onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 mb-1 self-start">
                {isValidCreatedAt ? createdAtTime : "Không xác định"}
              </span>
              <div className="flex items-start">
                <Link to={`/profile/${targetUser?.membershipId || targetUser?._id}`}>
                  <img
                    src={getAvatarUrl(targetUser)}
                    alt={targetUser?.name || "Sender Avatar"}
                    className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                    onError={(e) => (e.target.src = "https://placehold.co/50")}
                  />
                </Link>
                <div
                  className="relative w-fit max-w-md p-2 bg-white rounded-xl shadow-md border border-gray-200"
                  onContextMenu={(e) => handleContextMenu(e, msg)}
                >
                  {msg.replyTo && (
                    <div className="text-xs text-gray-500 mb-1 italic">
                      Trả lời: {messages.find((m) => m._id === msg.replyTo)?.content}
                    </div>
                  )}
                  <div className="text-gray-800" style={{ whiteSpace: 'pre-line' }}>
                    {adjustedDisplayContent}
                  </div>
                  {lines.length > maxLines && (
                    <button
                      onClick={toggleExpand}
                      className="text-blue-500 text-xs mt-1"
                    >
                      {isExpanded ? "Thu gọn" : "Xem thêm"}
                    </button>
                  )}
                  {isCurrentUserUserRole && adjustedDisplayContent !== "Tin nhắn đã được thu hồi" && (
                    <button
                      onClick={() => navigate("/upgrade")}
                      className="ml-2 text-blue-500 underline hover:text-blue-700"
                    >
                      Nâng cấp ngay
                    </button>
                  )}
                  {showLikeButton && (
                    <div className="absolute bottom-1 right-1">
                      {msg.liked ? (
                        <FaHeart
                          className="text-red-500 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                        />
                      ) : (
                        <FaHeart
                          className="text-gray-400 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ) : isGiftMessage ? (
          // Hiển thị tin nhắn quà tặng giống như tin nhắn văn bản
          msg.senderId !== authUser._id ? (
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 mb-1 self-start">
                {isValidCreatedAt ? createdAtTime : "Không xác định"}
              </span>
              <div className="flex items-start">
                <Link to={`/profile/${targetUser?.membershipId || targetUser?._id}`}>
                  <img
                    src={getAvatarUrl(targetUser)}
                    alt={targetUser?.name || "Sender Avatar"}
                    className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                    onError={(e) => (e.target.src = "https://placehold.co/50")}
                  />
                </Link>
                <div
                  className="relative w-fit max-w-md p-2 bg-white rounded-xl shadow-md border border-gray-200"
                  onContextMenu={(e) => handleContextMenu(e, msg)}
                >
                  <div className="text-gray-800">
                    <img
                      src={msg.gift.imageUrl}
                      alt={msg.gift.name}
                      className="w-24 h-24 object-cover rounded"
                      onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                    />
                    <p className="text-xs text-gray-500 mt-1">{msg.gift.name}</p>
                  </div>
                  {showLikeButton && (
                    <div className="absolute bottom-1 right-1">
                      {msg.liked ? (
                        <FaHeart
                          className="text-red-500 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg._id)}
                        />
                      ) : (
                        <FaHeart
                          className="text-gray-400 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg._id)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-end">
              <span className="text-xs text-gray-500 mb-1 self-end">
                {isValidCreatedAt ? createdAtTime : "Không xác định"}
              </span>
              <div
                className="relative w-fit max-w-md p-2 bg-green-500 text-white rounded-xl shadow-md"
                onContextMenu={(e) => handleContextMenu(e, msg)}
              >
                <div>
                  <img
                    src={msg.gift.imageUrl}
                    alt={msg.gift.name}
                    className="w-24 h-24 object-cover rounded"
                    onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                  />
                  <p className="text-xs mt-1">{msg.gift.name}</p>
                </div>
                {msg.liked && (
                  <div className="absolute bottom-1 right-1">
                    <FaHeart className="text-red-500" size={12} />
                  </div>
                )}
              </div>
              {msg._id === lastSenderMessageId && msg.read && (
                <div className="flex items-end mt-1">
                  <span className="text-xs text-gray-500 mr-1">
                    Đã xem {isValidReadTimestamp ? `lúc ${readTimestampTime}` : ""}
                  </span>
                  <Link to={`/profile/${receiverAvatar?.membershipId || receiverAvatar?._id}`}>
                    <img
                      src={getAvatarUrl(receiverAvatar)}
                      alt={receiverAvatar?.name || "Receiver Avatar"}
                      className="w-6 h-6 rounded-full object-cover border border-green-300 shadow-md"
                      onError={(e) => (e.target.src = "https://placehold.co/50")}
                    />
                  </Link>
                </div>
              )}
            </div>
          )
        ) : (
          // Giữ nguyên hiển thị tin nhắn văn bản
          msg.senderId !== authUser._id ? (
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 mb-1 self-start">
                {isValidCreatedAt ? createdAtTime : "Không xác định"}
              </span>
              <div className="flex items-start">
                <Link to={`/profile/${targetUser?.membershipId || targetUser?._id}`}>
                  <img
                    src={getAvatarUrl(targetUser)}
                    alt={targetUser?.name || "Sender Avatar"}
                    className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                    onError={(e) => (e.target.src = "https://placehold.co/50")}
                  />
                </Link>
                <div
                  className="relative w-fit max-w-md p-2 bg-white rounded-xl shadow-md border border-gray-200"
                  onContextMenu={(e) => handleContextMenu(e, msg)}
                >
                  {msg.replyTo && (
                    <div className="text-xs text-gray-500 mb-1 italic">
                      Trả lời: {messages.find((m) => m._id === msg.replyTo)?.content}
                    </div>
                  )}
                  <div className="text-gray-800" style={{ whiteSpace: 'pre-line' }}>
                    {displayContent}
                  </div>
                  {lines.length > maxLines && (
                    <button
                      onClick={toggleExpand}
                      className="text-blue-500 text-xs mt-1"
                    >
                      {isExpanded ? "Thu gọn" : "Xem thêm"}
                    </button>
                  )}
                  {showLikeButton && (
                    <div className="absolute bottom-1 right-1">
                      {msg.liked ? (
                        <FaHeart
                          className="text-red-500 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg._id)}
                        />
                      ) : (
                        <FaHeart
                          className="text-gray-400 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg._id)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-end">
              <span className="text-xs text-gray-500 mb-1 self-end">
                {isValidCreatedAt ? createdAtTime : "Không xác định"}
              </span>
              <div
                className="relative w-fit max-w-md p-2 bg-green-500 text-white rounded-xl shadow-md"
                onContextMenu={(e) => handleContextMenu(e, msg)}
              >
                {msg.replyTo && (
                  <div className="text-xs text-gray-200 mb-1 italic">
                    Trả lời: {messages.find((m) => m._id === msg.replyTo)?.content}
                  </div>
                )}
                <div style={{ whiteSpace: 'pre-line' }}>
                  {displayContent}
                </div>
                {lines.length > maxLines && (
                  <button
                    onClick={toggleExpand}
                    className="text-blue-200 text-xs mt-1"
                  >
                    {isExpanded ? "Thu gọn" : "Xem thêm"}
                  </button>
                )}
                {msg.liked && (
                  <div className="absolute bottom-1 right-1">
                    <FaHeart className="text-red-500" size={12} />
                  </div>
                )}
              </div>
              {msg._id === lastSenderMessageId && msg.read && (
                <div className="flex items-end mt-1">
                  <span className="text-xs text-gray-500 mr-1">
                    Đã xem {isValidReadTimestamp ? `lúc ${readTimestampTime}` : ""}
                  </span>
                  <Link to={`/profile/${receiverAvatar?.membershipId || receiverAvatar?._id}`}>
                    <img
                      src={getAvatarUrl(receiverAvatar)}
                      alt={receiverAvatar?.name || "Receiver Avatar"}
                      className="w-6 h-6 rounded-full object-cover border border-green-300 shadow-md"
                      onError={(e) => (e.target.src = "https://placehold.co/50")}
                    />
                  </Link>
                </div>
              )}
            </div>
          )
        )}
      </div>
    );
  };

  if (!authUser) {
    return <p className="text-center text-gray-600">Đang tải thông tin người dùng...</p>;
  }

  if (loading) {
    return <p className="text-center text-gray-600">Đang tải dữ liệu chat...</p>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans flex items-center justify-center">
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleBackToChatList}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-300 mr-2"
          >
            Quay lại danh sách trò chuyện
          </button>
          {error.includes("Phiên làm việc hết hạn") && (
            <button
              onClick={() => navigate("/login")}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-300"
            >
              Đăng nhập lại
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 font-sans" onClick={closeContextMenu}>
      {notification && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 text-white ${
            isNotiFading ? "animate-fade-out" : "animate-slide-in"
          } ${notification.type === "success" ? "bg-green-500" : notification.type === "warning" ? "bg-yellow-500" : notification.type === "info" ? "bg-blue-500" : "bg-red-500"}`}
        >
          {notification.message}
          {notification.message.includes("Phiên làm việc hết hạn") && (
            <button
              onClick={() => navigate("/login")}
              className="ml-2 px-2 py-1 bg-white text-red-500 rounded hover:bg-gray-200"
            >
              Đăng nhập lại
            </button>
          )}
        </div>
      )}
      {isReconnecting && (
        <div className="fixed top-4 left-4 px-4 py-2 rounded-lg shadow-lg z-50 text-white bg-yellow-500">
          Đang kết nối lại...
        </div>
      )}
      <Navbar
        unreadCount={totalUnread}
        unreadNotificationCount={unreadNotificationCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />
      <div className="pt-16">
        <ChatTabs unreadCount={totalUnread} onTabChange={() => {}} />
      </div>
      <div className="flex pt-4">
        <div className="w-1/4 bg-white shadow-xl p-4 fixed h-screen rounded-r-2xl">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <Link to={`/profile/${targetUser?.membershipId || targetUser?._id}`}>
                <img
                  src={getAvatarUrl(targetUser)}
                  alt={targetUser?.name || "User Avatar"}
                  className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-green-300 shadow-md"
                  onError={(e) => (e.target.src = "https://placehold.co/50")}
                />
              </Link>
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center">
                  {targetUser?.name || "Người dùng"}
                  {(targetUser?.role === "vip_gold" ||
                    targetUser?.role === "vip_platinum" ||
                    targetUser?.role === "vip_diamond") && (
                    <FaCrown className={`ml-2 ${getVipIconColor(targetUser.role)}`} />
                  )}
                </h3>
                <span className="text-sm text-gray-500">
                  {targetUser?.isOnline ? "Trực tuyến" : "Ngoại tuyến"} • {targetUser?.locationText?.city || "Không xác định"}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 hover:shadow-md transition duration-200">
              Trực tuyến
            </button>
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 hover:shadow-md transition duration-200">
              Tìm kiếm
            </button>
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 hover:shadow-md transition duration-200">
              Hoạt động
            </button>
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 hover:shadow-md transition duration-200">
              Livestream
            </button>
          </div>
        </div>
        <div className="w-3/4 ml-[25%] flex flex-col">
          <div className="bg-gradient-to-r from-green-100 to-gray-100 p-4 shadow-md flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              Trò chuyện với {targetUser?.name || "Người dùng"}
              {(targetUser?.role === "vip_gold" ||
                targetUser?.role === "vip_platinum" ||
                targetUser?.role === "vip_diamond") && (
                <FaCrown className={`ml-2 ${getVipIconColor(targetUser.role)}`} />
              )}
            </h2>
            <div>
              <button
                onClick={handleDeleteAllMessages}
                className="px-2 py-1 bg-red-700 text-white rounded hover:bg-red-800 transition duration-200"
              >
                <FaTrash className="inline mr-1" /> Xóa tất cả
              </button>
            </div>
          </div>
          <div className="flex-1 px-6 pb-6 overflow-y-auto bg-gray-100 flex flex-col">
            <div ref={chatStartRef} />
            {pinnedMessages.length > 0 && messages.some((msg) => pinnedMessages.includes(msg._id)) && (
              <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Tin nhắn được ghim</h3>
                {messages
                  .filter((msg) => pinnedMessages.includes(msg._id))
                  .map((msg) => (
                    <div
                      key={msg._id}
                      className="p-2 bg-yellow-100 rounded mb-2"
                      onContextMenu={(e) => handleContextMenu(e, msg)}
                    >
                      <div className="text-gray-800" style={{ whiteSpace: 'pre-line' }}>{msg.content}</div>
                      <p className="text-xs text-gray-500">
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                  ))}
              </div>
            )}
            {messages.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-gray-500">Chưa có tin nhắn nào. Hãy bắt đầu trò chuyện!</p>
              </div>
            ) : (
              <FixedSizeList
                height={window.innerHeight - 300}
                itemCount={messages.length}
                itemSize={100}
                width="100%"
                style={{ flex: '1 1 auto' }}
              >
                {Row}
              </FixedSizeList>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="bg-white p-4 shadow-lg">
  {replyToMessage && (
    <div className="mb-2 p-2 bg-gray-200 rounded flex justify-between items-center border border-gray-300">
      <div>
        <p className="text-sm text-gray-600">Đang trả lời: {replyToMessage.content}</p>
      </div>
      <button
        onClick={() => setReplyToMessage(null)}
        className="text-red-500 hover:text-red-700"
      >
        Hủy
      </button>
    </div>
  )}
  <div className="flex items-center">
    <button
      onClick={() => setShowGiftModal(true)}
      className="p-3 bg-purple-500 text-white rounded-l-xl hover:bg-purple-600 transition duration-300"
      title="Gửi quà tặng"
    >
      <FaGift />
    </button>
    <textarea
      value={newMessage}
      onChange={(e) => setNewMessage(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={
        authUser.role === "user"
          ? "Nhập tin nhắn..."
          : "Nhập tin nhắn..."
      }
      className="flex-1 p-3 border rounded-none focus:outline-none focus:ring-2 focus:ring-green-400 resize-none h-14 bg-gray-50"
      rows="1"
    />
    <ChatRestriction
      userId={userId}
      token={token}
      messages={messages}
      onSendMessage={handleSendMessage}
      authUser={authUser}
      targetUser={targetUser}
      setMessages={setMessages}
      newMessage={newMessage}
      fetchWithRetry={fetchWithRetry}
    />
  </div>
  {showGiftModal && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
        <h3 className="text-lg font-semibold mb-4">Chọn quà tặng</h3>
        <SendGift
          selectedUserId={{ id: userId }}
          onClose={() => setShowGiftModal(false)}
          onGiftSent={handleSendGift}
        />
      </div>
    </div>
  )}
</div>
        </div>
      </div>
      <AnimatePresence>
        {contextMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            ref={(node) => {
              if (node) {
                const menuWidth = node.offsetWidth;
                const menuHeight = node.offsetHeight;
                const windowWidth = window.innerWidth;
                const windowHeight = window.innerHeight;
                let adjustedX = contextMenu.x;
                let adjustedY = contextMenu.y;

                if (contextMenu.x + menuWidth > windowWidth) {
                  adjustedX = windowWidth - menuWidth - 10;
                }

                if (contextMenu.y + menuHeight > windowHeight) {
                  adjustedY = windowHeight - menuHeight - 10;
                }

                node.style.left = `${adjustedX}px`;
                node.style.top = `${adjustedY}px`;
              }
            }}
            className="fixed bg-white shadow-lg rounded-lg p-2 z-50 border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedMessages.length > 0 ? (
              <>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={handleDeleteSelectedMessages}
                >
                  <FaTrash className="mr-2" /> Xóa {selectedMessages.length} tin nhắn đã chọn
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={handleShareSelectedMessages}
                >
                  <FaShare className="mr-2" /> Chia sẻ các tin nhắn đã chọn
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer text-gray-500"
                  onClick={() => setSelectedMessages([])}
                >
                  <span>Hủy chọn</span>
                </div>
              </>
            ) : (
              <>
                <div className="text-gray-500 text-xs p-2 italic">
                  Nhấn Ctrl + Click để chọn nhiều tin nhắn
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleLikeMessage(contextMenu.messageId)}
                >
                  <FaHeart className="mr-2" /> Like
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleDeleteMessage(contextMenu.messageId)}
                >
                  <FaTrash className="mr-2" /> Xóa tin nhắn
                </div>
                {messages.find(msg => msg._id === contextMenu.messageId)?.senderId === authUser._id && (
                  <div
                    className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleRecallMessage(contextMenu.messageId)}
                  >
                    <FaUndo className="mr-2" /> Thu hồi tin nhắn
                  </div>
                )}
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={handleDeleteAllMessages}
                >
                  <FaTrash className="mr-2" /> Xóa tất cả tin nhắn
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleCopyMessage(contextMenu.messageContent)}
                >
                  <FaCopy className="mr-2" /> Sao chép
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleReplyMessage(contextMenu.messageId)}
                >
                  <FaReply className="mr-2" /> Trả lời
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleShareMessage(contextMenu.messageId)}
                >
                  <FaShare className="mr-2" /> Chia sẻ
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handlePinMessage(contextMenu.messageId)}
                >
                  <FaThumbtack className="mr-2" /> {pinnedMessages.includes(contextMenu.messageId) ? "Bỏ ghim" : "Ghim tin nhắn"}
                </div>
                <div
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleMarkMessage(contextMenu.messageId)}
                >
                  <FaStar className="mr-2" /> {markedMessages.includes(contextMenu.messageId) ? "Bỏ đánh dấu" : "Đánh dấu tin nhắn"}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatPage;